/**
 * useSpotProjectionRealtime — globaler Realtime-Listener.
 *
 * Hört ausschließlich auf Änderungen an der Lese-Projektion
 * `spot_search_projection` und invalidiert alle React-Query-Caches, die diese
 * Daten nutzen.
 *
 * Wichtig: Wir abonnieren bewusst NICHT die Source-Tabellen (`spots`,
 * `access_points`, `waterbodies`, `places`). Source-Updates lösen serverseitig
 * Rebuild-Jobs aus, die die Projection einige Sekunden später aktualisieren.
 * Würden wir bereits beim Source-UPDATE invalidieren, gewinnt der Refetch das
 * Rennen gegen den Rebuild-Job und holt veraltete Projection-Daten (z. B.
 * alte Geo-Koordinaten auf der Karte, die erst nach einem zweiten Trigger
 * wie dem Status-Switch korrekt erscheinen).
 */
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtimeChannel, type RealtimeChannelBinding } from '@/hooks/useRealtimeChannel';

const PROJECTION_QUERY_KEYS = [
  ['guide-spots-projection'],
  ['map-spots-projection'],
  ['discovery-spots-projection'],
  ['searchable-spots-projection'],
  ['admin-spot-list-projection'],
  ['admin-spot-list-projection-v2'],
  ['admin-spot-quality-queue'],
  ['v2-migration-factory'],
  ['normalized-spots'],
];

export function useSpotProjectionRealtime() {
  const queryClient = useQueryClient();

  const invalidateAll = useCallback(() => {
    for (const key of PROJECTION_QUERY_KEYS) {
      // refetchType: 'active' forces visible queries (Guide list, Map) to
      // immediately refetch instead of only being marked stale. This is
      // what keeps the map consistent during rapid publish/unpublish
      // switches — the next render no longer wins the race against the
      // background refetch.
      queryClient.invalidateQueries({ queryKey: key, refetchType: 'active' });
    }
  }, [queryClient]);

  // Burst-debounce so a multi-row write doesn't trigger N invalidations.
  const debouncedInvalidate = useMemo(() => {
    let scheduled = false;
    return () => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => {
        scheduled = false;
        invalidateAll();
      }, 250);
    };
  }, [invalidateAll]);

  const bindings = useMemo<RealtimeChannelBinding[]>(
    () => [
      { postgresChanges: { event: '*', schema: 'public', table: 'spot_search_projection' }, handler: debouncedInvalidate },
    ],
    [debouncedInvalidate],
  );

  useRealtimeChannel({
    // Topic-Konvention (Schritt 04 Pre-Open-Beta): public-projection -> `pub:*`
    topic: 'pub:spot-projection',
    bindings,
    // Polling-Fallback: nach 6 vergeblichen Reconnects alle 30s einmalig invalidieren.
    onPollingTick: invalidateAll,
  });
}
